import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  EditOutlined,
  EyeOutlined,
  LikeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Divider,
  Flex,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import { useQuery } from '@apollo/client/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import TransunionLogo from 'assets/transunion.svg?react';
import SocureLogo from 'assets/socure-logo.svg?react';

import EditCustomerInfoModal from './EditCustomerInfoModal';
import DocVModal from './DocVModal';
import OpportunityPanel from './OpportunityPanel';
import SendEmailModal from './SendEmailModal';
import TransUnionModal from './TransUnionModal';

import { SidebarDivider } from 'layouts/DashboardLayout/Sidebar';
import ClientMetaSkeleton from 'components/ClientMetaSkeleton/ClientMetaSkeleton';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import AlertTooltip from 'components/AlertTooltip/AlertTooltip';
import LocationAndTime from 'components/LocationAndTime/LocationAndTime';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import useAnalytics from 'hooks/useAnalytics';
import useCeaseAndDesistStatus from 'hooks/useCeaseAndDesistStatus';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useCustomerFullSsn from 'hooks/useCustomerFullSsn';
import useModalControls from 'hooks/useModalControls';

import { CUSTOMER_FINANCIAL_INFO } from 'queries/customer';
import { FRAUD_CASES_SIMPLE } from 'queries/fraudCases';

import { getAgeFromDate, formatDate } from 'utilities/datesAndTimes';
import { getFraudStatus } from 'utilities/helpers';
import { ANALYTICS_EVENTS, FRAUD_STATUS, SIZE } from 'utilities/constants';

const CustomerMeta = () => {
  const { customerId } = useParams();
  const url = `/customers/${customerId}`;
  const { customerInfo, customerInfoQuery } = useCustomerInfo();
  const [fullSsn, getFullSsn, ssnFailureMessage, { loading: loadingFullSsn }] =
    useCustomerFullSsn();
  const { ceaseAndDesistEnabled } = useCeaseAndDesistStatus();
  const { trackEvent } = useAnalytics();
  const {
    showDocVModal,
    enableOpportunitiesPanel,
    enableFraudCaseMgrDecommission,
  } = useFlags();

  const [activeFraudCases, setActiveFraudCases] = useState([]);

  const [address, ecsId] = useMemo(() => {
    return [customerInfo?.address || {}, customerInfo.id];
  }, [customerInfo]);

  const editCustomerInfoModal = useModalControls({
    onHide: () => trackEvent(ANALYTICS_EVENTS.EDIT_CUSTOMER_INFO_CLOSED),
    onShow: () => trackEvent(ANALYTICS_EVENTS.EDIT_CUSTOMER_INFO_OPENED),
  });

  const sendEmailModal = useModalControls({
    onHide: () => trackEvent(ANALYTICS_EVENTS.AUTOMATED_EMAIL_CLOSED),
    onShow: () => trackEvent(ANALYTICS_EVENTS.AUTOMATED_EMAIL_OPENED),
  });

  const transunionModal = useModalControls();
  const docVModal = useModalControls();

  const street2 = address.street2 ? ` ${address.street2}` : '';
  const streetAddress = `${address.street1}${street2}`;
  const cityStateZip = `${address.city}, ${address.state} ${address.postalCode}`;
  const fullAddress = `${streetAddress} ${cityStateZip}`;

  let { data: fraudData } = useQuery(FRAUD_CASES_SIMPLE, {
    variables: { customerId },
    skip: enableFraudCaseMgrDecommission,
  });

  const financialInfoQuery = useQuery(CUSTOMER_FINANCIAL_INFO, {
    variables: { customerId },
    notifyOnNetworkStatusChange: true,
  });

  const shouldGetCLIPTout = useMemo(() => {
    return (
      financialInfoQuery.data?.customer?.financialInfo?.shouldGetCLIPTout ||
      false
    );
  }, [financialInfoQuery.data]);

  const getCustomerName = (short = false) => {
    let { firstName, middleName, lastName } = customerInfo;
    middleName = middleName || '';
    const middle = short ? middleName.charAt(0) : middleName;
    return `${firstName} ${middle} ${lastName}`;
  };

  // Handling fraud data in useEffect since refetch is not
  // triggered from other components (like the Fraud forms)
  useEffect(() => {
    if (fraudData) {
      setActiveFraudCases(
        fraudData.fraudCasesV2.filter((fraudCase) => {
          let active = true;
          switch (getFraudStatus(fraudCase.status)) {
            case FRAUD_STATUS.CLEARED:
            case FRAUD_STATUS.RESTORED:
            case FRAUD_STATUS.REJECTED:
              active = false;
              break;
          }
          return active;
        }),
      );
    }
  }, [fraudData]);

  const renderAlerts = () => {
    let alerts = [];

    if (shouldGetCLIPTout) {
      alerts.push(
        <Alert
          type="warning"
          data-testid="clip-tout-alert"
          title={
            <div>
              The customer's Income and Residence information requires an{' '}
              <Link to={`${url}/details`} data-testid="income-residence-link">
                update
              </Link>
            </div>
          }
        ></Alert>,
      );
    }

    if (ceaseAndDesistEnabled) {
      alerts.push(
        <Alert
          data-testid="cease-and-desist-message"
          type="warning"
          title="Cease & Desist"
        ></Alert>,
      );
    }

    if (!enableFraudCaseMgrDecommission && activeFraudCases.length) {
      alerts = alerts.concat(
        activeFraudCases.map((fraudCase) => {
          let type = fraudCase.type.includes('Fraud')
            ? fraudCase.type
            : `${fraudCase.type} Fraud`;
          return (
            <Alert
              data-testid={`fraud-${fraudCase.caseHash}`}
              key={`fraud-${fraudCase.caseHash}`}
              type="error"
              title={
                <Typography.Text>
                  {type}
                  <br></br>
                  {getFraudStatus(fraudCase.status)}
                </Typography.Text>
              }
              action={
                <Link
                  data-testid="fraud-link"
                  to={`${url}/fraud/case/${fraudCase.caseHash}`}
                >
                  Details
                </Link>
              }
            ></Alert>
          );
        }),
      );
    }

    return alerts.length ? (
      <>
        <Flex vertical={true} gap="var(--spacing-xs)">
          {alerts}
        </Flex>
        <SidebarDivider />
      </>
    ) : null;
  };

  return (
    <>
      {/* Modals */}
      <EditCustomerInfoModal
        {...editCustomerInfoModal}
        onSuccess={customerInfoQuery.refetch}
      />
      <SendEmailModal {...sendEmailModal} />
      <TransUnionModal {...transunionModal} />
      {showDocVModal && <DocVModal {...docVModal} />}

      {/* Customer Information */}
      <QueryBoundary
        messageHeight="372px"
        errorMessage="Failed to load Customer Information."
        query={customerInfoQuery}
        skeleton={ClientMetaSkeleton}
      >
        <Flex vertical={true}>
          <Flex align="center" justify="space-between" gap="var(--spacing)">
            <div>
              <Typography.Title
                data-testid="customer-name"
                level={3}
                style={{
                  margin: 0,
                }}
                copyable={{
                  tooltips: [
                    'Copy full name',
                    <span>
                      Full name copied <LikeOutlined />
                    </span>,
                  ],
                  text: `${getCustomerName()}`,
                }}
              >
                <Tooltip
                  title={<span className="fs-mask">{getCustomerName()}</span>}
                >
                  <span className="fs-mask">{getCustomerName(true)}</span>
                </Tooltip>
              </Typography.Title>
            </div>
            <Tooltip title={'Edit customer information'}>
              <Button
                data-testid="edit-customer-info"
                type="link"
                icon={<EditOutlined style={{ fontSize: '1.4rem' }} />}
                onClick={editCustomerInfoModal.show}
                style={{ width: '1.4rem' }}
              />
            </Tooltip>
          </Flex>

          <Flex vertical={true} gap="var(--spacing-xs)">
            <Typography.Text
              copyable={{
                text: `${ecsId}`,
              }}
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'normal',
              }}
            >
              Profile ID: {ecsId}
            </Typography.Text>

            <Flex justify="space-between">
              <Flex align="center" gap="var(--spacing-xs)">
                <Tooltip title="View TransUnion customer info">
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 'var(--spacing-xs)' }}
                    onClick={transunionModal.show}
                  >
                    <TransunionLogo
                      style={{
                        width: 'var(--font-size-lg)',
                        height: 'var(--font-size-lg)',
                      }}
                    />
                  </Button>
                </Tooltip>

                {showDocVModal ? (
                  <>
                    <Divider
                      orientation="vertical"
                      style={{ marginInline: '0px' }}
                    />

                    <Tooltip title="Generate DocV Link">
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 'var(--spacing-xs)' }}
                        onClick={docVModal.show}
                        data-testid="open-docv-modal-button"
                      >
                        <SocureLogo
                          style={{
                            height: '60px',
                            width: 'auto',
                          }}
                        />
                      </Button>
                    </Tooltip>
                  </>
                ) : null}
              </Flex>

              {enableOpportunitiesPanel ? <OpportunityPanel /> : undefined}
            </Flex>
          </Flex>

          <SidebarDivider />

          <LocationAndTime client={customerInfo}></LocationAndTime>

          <SidebarDivider />

          {renderAlerts()}

          <Row gutter={[10, '1rem']}>
            <Col sm={11}>
              <AlertTooltip
                type="error"
                placement="topLeft"
                open={!!ssnFailureMessage}
                title={ssnFailureMessage}
                styles={{
                  root: {
                    position: 'fixed',
                  },
                }}
              >
                <div>
                  <DisplayValue
                    label="SSN"
                    labelIcon={loadingFullSsn ? LoadingOutlined : EyeOutlined}
                    onLabelIconClick={loadingFullSsn ? undefined : getFullSsn}
                    size={SIZE.MD}
                  >
                    {fullSsn ? (
                      <span className="fs-mask">{fullSsn}</span>
                    ) : (
                      `_____ ${customerInfo.lastFourSSN}`
                    )}
                  </DisplayValue>
                </div>
              </AlertTooltip>
            </Col>
            <Col sm={13}>
              <DisplayValue
                label="Primary Phone"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={`${customerInfo.phoneNumber}`.slice(2)}
              >
                <span className="fs-mask">{customerInfo.phoneNumber}</span>
              </DisplayValue>
            </Col>
            <Col sm={11}>
              <DisplayValue
                label="Date of Birth"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={formatDate(customerInfo.dateOfBirth)}
              >
                {formatDate(customerInfo.dateOfBirth)} &bull;{' '}
                {getAgeFromDate(customerInfo.dateOfBirth)} yrs
              </DisplayValue>
            </Col>
            <Col sm={13}>
              <DisplayValue
                label="Email"
                onValueClick={sendEmailModal.show}
                canCopy={true}
                copiedValue={customerInfo.email}
                size={SIZE.MD}
              >
                <span className="fs-mask">{customerInfo.email}</span>
              </DisplayValue>
            </Col>
            <Col sm={24}>
              <DisplayValue
                label="Address"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={fullAddress}
              >
                <span className="fs-mask">
                  {streetAddress}
                  <br></br>
                  {cityStateZip}
                </span>
              </DisplayValue>
            </Col>
          </Row>
        </Flex>
      </QueryBoundary>
    </>
  );
};

export default CustomerMeta;
